import config from '@/config'
import { PageRequest, PageResponse } from '@/types/Support'
import api, { getRefreshToken } from '@/api'
import { Catalog, CatalogCatalogFilters } from '@/types/catalogs/Catalogs'
import { AsyncTask, AsyncTaskFilters, AsyncTaskType } from '@/types/catalogs/AsyncTask'
import { DateUtils } from './DateFormat'

class CatalogsFillsService {
  /**
   * Gets all the catalog records by the Fills API
   *
   * @param pageRequest with configurations for the request
   * @returns a object with the request response
   */
  async getCatalogs (pageRequest: PageRequest<CatalogCatalogFilters>)
  : Promise<PageResponse<Catalog>> {
    let url = `${config.apiFillsUrl}catalogs`
    const filters = pageRequest.filters
    url += `?limit=${pageRequest.size}`
    url += `&offset=${pageRequest.page * pageRequest.size}`
    url += filters.sort ? `&sort=${filters.sort}` : ''
    url += filters.domain && filters.domain.id >= 0
      ? `&domain=${filters.domain.id}` : ''
    url += filters.project && filters.project > 0
      ? `&project=${filters.project}` : ''
    url += filters.name ? `&name=${filters.name}` : ''
    url += filters.version && filters.version > 0
      ? `&version=${filters.version}` : ''
    url += filters.grouping
      ? `&grouping=${filters.grouping}` : ''

    const response = await api.get(url)
    return response.data
  }

  /**
   * Gets the catalog with the id that recives as parameter
   *
   * @param id from the catalog to get
   * @returns a catalog values
   */
  async getCatalog (id: number) : Promise<Catalog> {
    const response = await api.get(`${config.apiFillsUrl}catalogs/${id}`)
    return response.data
  }

  /**
   * Creates or save the catalog form
   *
   * @param catalog to create or save changes
   * @returns the catalog saved
   */
  async saveCatalog (catalog: Catalog) : Promise<Catalog> {
    const request = {
      name: catalog.name,
      domain: catalog.domain,
      project: catalog.project,
      version: catalog.version,
      source: catalog.source,
      description: catalog.description,
      data: catalog.data,
      grouping: catalog.grouping,
      generalDomain: catalog.generalDomain
    }
    const response = catalog.id
      ? await api.put(`${config.apiFillsUrl}catalogs/${catalog.id}`, request)
      : await api.post(`${config.apiFillsUrl}catalogs`, request)
    return response.data
  }

  /**
   * Update the catalog form
   *
   * @param catalog to update changes
   * @returns the update saved
   */
  async updateCatalog (catalog: Catalog) : Promise<Catalog> {
    const request = {
      name: catalog.name,
      domain: catalog.domain,
      project: catalog.project,
      version: catalog.version,
      source: catalog.source,
      description: catalog.description,
      data: catalog.data,
      grouping: catalog.grouping
    }
    const response = await api.patch(`${config.apiFillsUrl}catalogs/${catalog.id}`, request)
    return response.data
  }
  /**
   * Deletes the catalog with the id that recives as parameter
   *
   * @param id from the catalog to delete
   * @returns deleted catalog values
   */
  async deleteCatalog (id: number) : Promise<Catalog> {
    const response = await api.delete(`${config.apiFillsUrl}catalogs/${id}`,
      { headers: { 'Content-Type': 'application/json' }, data: null })
    return response.data
  }

  /**
   * Create a new async task with the values that received as param
   *
   * @param taskConfig configuration to create an async task
   * @returns an object with the task values
   */
  async createAsyncTask (taskConfig: AsyncTask) {
    const defaultTimeZone = DateUtils.getTimezoneOffsetWithSignHHmm()
    const data = {
      name: taskConfig.name,
      type: taskConfig.type,
      template: taskConfig.template.id,
      process: {
        attachmentIds: taskConfig.process.attachmentIds,
        attachmentFormat: taskConfig.process.attachmentFormat,
        search: taskConfig.process.search || null,
        format: taskConfig.process.format || null,
        timeZone: taskConfig.process.timeZone || defaultTimeZone,
        header: taskConfig.process.header || null
      }
    }
    const endpoint = `${config.apiFillsUrl}tasks`
    const headers = this.makeAsyncTaskPOSTRequestHeaders()
    const response = await api.post(endpoint, data, headers)
    return response.data
  }

  /**
   * Set the headers values for the POST request to create a task async
   *
   * @returns an object with headers configurations for a POST request
   */
  private makeAsyncTaskPOSTRequestHeaders (): Object {
    const headers = {
      headers: {}
    }
    this.setJWTRefreshToken(headers.headers)
    return headers
  }

  /**
   * Set the refresh token into the 'headers' object
   *
   * @param headers object where to set the refreshToken
   * @returns object with headers values
   */
  private setJWTRefreshToken (headers: any): Object {
    const refreskToken = getRefreshToken()
    if (refreskToken) {
      headers[config.headers.jwtRefreshToken] = refreskToken
    }
    return headers
  }

  /**
   * Get an async task by its id
   *
   * @param id to get task
   * @returns an AsyncTask
   */
  async getAsyncTaskById (id: number): Promise<AsyncTask> {
    const endpoint = `${config.apiFillsUrl}tasks/${id}`
    const response = await api.get(endpoint)
    return response.data as AsyncTask
  }

  /**
   * Get url of result of AsyncTask by Id
   *
   * @param id to get task
   * @returns an string
   */
  async getResultByID (id: number): Promise<any> {
    const endpoint = `${config.apiFillsUrl}tasks/${id}/results`
    const response = await api.get(endpoint)
    return response.data as string
  }

  /**
   * Get all the async tasks that exist with the configurations send it
   *
   * @param page with the configurations to apply
   * @returns page response with a list of async tasks
   */
  async getAsyncTasks (page: PageRequest<AsyncTaskFilters>): Promise<PageResponse<AsyncTask>> {
    const filters = page.filters
    const params = new URLSearchParams()
    params.append('sort', page.sort || 'id')
    params.append('limit', (page.size).toString())
    params.append('offset', (page.page * page.size).toString())
    if (filters.name) params.append('name', filters.name)
    if (filters.type && filters.type !== AsyncTaskType.NONE) params.append('type', filters.type)
    if (filters.status) params.append('status', filters.status)
    if (filters.template !== undefined && filters.template > 0) params.append('template', filters.template.toString())
    if (filters.domain !== undefined && filters.domain >= 0) params.append('domain', filters.domain.toString())
    if (filters.project !== undefined && filters.project > 0) params.append('project', filters.project.toString())
    if (filters.user !== undefined && filters.user > 0) params.append('user', filters.user.toString())
    const endpoint = `${config.apiFillsUrl}tasks`
    const response = await api.get(endpoint, { params })
    return response.data
  }

  /**
   * Delete (canceled) a task with 'id'
   *
   * @param taskId of the task to cancel
   * @returns task canceled resume
   */
  async deleteTask (taskId: number): Promise<AsyncTask> {
    const endpoint = `${config.apiFillsUrl}tasks/${taskId}`
    const response = await api.delete(endpoint,
      { headers: { 'Content-Type': 'application/json' }, data: null })
    return response.data
  }

  /**
   * Call the verification process to complete unfinish asynctasks
   *
   * @returns a list with the async task processed
   */
  async taskVerification () : Promise<PageResponse<AsyncTask>> {
    const endpoint = `${config.apiFillsUrl}tasks/verification`
    return api.post(endpoint, {})
      .then((response) => {
        return response.data
      })
  }
}
export default new CatalogsFillsService()
